import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  Settings, Shield, Key, Globe, CheckCircle, Loader2, Save, Eye, EyeOff, Link2,
  Star, Play, TrendingUp, RotateCcw
} from "lucide-react";

export default function SettingsPage() {
  const { api } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const [form, setForm] = useState({
    api_key: "",
    base_url: "http://local.adspower.net:50325",
    default_profile_id: "",
    enabled: false,
  });

  // Bulk grade state
  const [gradeLimit, setGradeLimit] = useState(50);
  const [grading, setGrading] = useState(false);
  const [gradeResult, setGradeResult] = useState(null);

  // Organic traffic state
  const [organicRunning, setOrganicRunning] = useState(false);
  const [organicTaskId, setOrganicTaskId] = useState(null);
  const [organicProgress, setOrganicProgress] = useState(null);

  // Revalidate-deleted state
  const [revalRunning, setRevalRunning] = useState(false);
  const [revalProgress, setRevalProgress] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get("/settings/adspower", { timeout: 5000 });
      setSettings(res.data);
      setForm({
        api_key: res.data.api_key || "",
        base_url: res.data.base_url || "http://local.adspower.net:50325",
        default_profile_id: res.data.default_profile_id || "",
        enabled: res.data.enabled || false,
      });
    } catch (err) {
      console.error("Settings fetch error (non-blocking):", err?.message);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put("/settings/adspower", form);
      setSaved(true);
      fetchSettings();
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleBulkGrade = async () => {
    setGrading(true);
    setGradeResult(null);
    try {
      const res = await api.post(`/llm/auto-grade-ungraded?limit=${gradeLimit}`);
      setGradeResult(res.data);
    } catch (err) {
      setGradeResult({ error: err.response?.data?.detail || "Failed to start grading" });
    } finally {
      setGrading(false);
    }
  };

  const handleBulkOrganic = async () => {
    setOrganicRunning(true);
    setOrganicProgress(null);
    try {
      const res = await api.post("/keywords/organic-traffic", { project_id: "__all__" });
      if (res.data?.task_id) {
        setOrganicTaskId(res.data.task_id);
        setOrganicProgress({ status: "processing", queued: res.data.queued_entries, completed_urls: 0, updated_entries: 0 });
        // Start polling
        const pollId = setInterval(async () => {
          try {
            const sr = await api.get(`/keywords/organic-traffic-status/${res.data.task_id}`);
            setOrganicProgress(sr.data);
            if (sr.data.status === "completed" || sr.data.status === "failed") {
              clearInterval(pollId);
              setOrganicRunning(false);
            }
          } catch {
            clearInterval(pollId);
            setOrganicRunning(false);
          }
        }, 10000);
      } else {
        setOrganicProgress({ status: "done", message: res.data?.message || "No entries to process" });
        setOrganicRunning(false);
      }
    } catch (err) {
      setOrganicProgress({ status: "error", message: err.response?.data?.detail || "Failed" });
      setOrganicRunning(false);
    }
  };

  const handleRevalidateDeleted = async () => {
    if (!window.confirm("Re-check every entry currently marked as 'deleted' against Reddit? Entries confirmed live will flip back to live. Rate-limited to ~100 req/min, so expect a few minutes for large backlogs.")) return;
    setRevalRunning(true);
    setRevalProgress(null);
    try {
      const res = await api.post("/sync/revalidate-deleted");
      if (res.data?.status === "empty") {
        setRevalProgress({ status: "done", message: res.data.message });
        setRevalRunning(false);
        return;
      }
      if (res.data?.status === "busy") {
        // Attach to the already-running task
        pollReval(res.data.task_id);
        return;
      }
      if (res.data?.task_id) {
        setRevalProgress({ status: "processing", total: res.data.total, checked: 0, flipped_to_live: 0, still_deleted: 0, errors: 0 });
        pollReval(res.data.task_id);
      }
    } catch (err) {
      setRevalProgress({ status: "error", message: err.response?.data?.detail || "Failed" });
      setRevalRunning(false);
    }
  };

  const pollReval = (taskId) => {
    const poll = setInterval(async () => {
      try {
        const sr = await api.get(`/sync/revalidate-deleted-status/${taskId}`);
        setRevalProgress(sr.data);
        if (sr.data.status === "completed" || sr.data.status === "failed") {
          clearInterval(poll);
          setRevalRunning(false);
        }
      } catch {
        clearInterval(poll);
        setRevalRunning(false);
      }
    }, 5000);
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#71717a" }}>
        <Loader2 size={24} className="animate-spin" style={{ margin: "0 auto 12px", display: "block" }} />
        Loading settings...
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 0", maxWidth: 640 }} data-testid="settings-page">
      <h2 style={{ fontSize: 16, fontWeight: 600, color: "#fafafa", display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
        <Settings size={18} /> App Settings
      </h2>

      {/* Bulk AI Grading */}
      <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 24, marginBottom: 20 }} data-testid="bulk-grade-section">
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#fafafa", display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <Star size={16} color="#a78bfa" /> Bulk AI Grading
        </h3>
        <p style={{ fontSize: 12, color: "#71717a", marginBottom: 16, lineHeight: 1.5 }}>
          Grade all ungraded live entries against their project brief using Grok 4.3. Runs in background.
          The auto-grade cron also grades 20 entries every 6 hours automatically.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 12, color: "#a1a1aa" }}>Batch size:</label>
            <input
              type="number" min="5" max="500" value={gradeLimit}
              onChange={e => setGradeLimit(Math.max(5, Math.min(500, parseInt(e.target.value) || 50)))}
              className="filter-input"
              style={{ width: 80, textAlign: "center" }}
              data-testid="grade-limit-input"
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleBulkGrade}
            disabled={grading}
            data-testid="bulk-grade-trigger-btn"
            style={{ background: "#7c3aed" }}
          >
            {grading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {grading ? " Starting..." : " Grade Ungraded Entries"}
          </button>
        </div>
        {gradeResult && (
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 6, fontSize: 12, lineHeight: 1.5,
            background: gradeResult.error ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.1)",
            border: `1px solid ${gradeResult.error ? "rgba(239,68,68,0.2)" : "rgba(16,185,129,0.2)"}`,
            color: gradeResult.error ? "#f87171" : "#34d399",
          }} data-testid="grade-result">
            {gradeResult.error ? gradeResult.error : (
              <>Queued <strong>{gradeResult.queued}</strong> entries for grading in background.
              {gradeResult.total_ungraded > gradeResult.queued && (
                <> ({gradeResult.total_ungraded - gradeResult.queued} more remaining — run again after this batch completes)</>
              )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Bulk Organic Traffic */}
      <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 24, marginBottom: 20 }} data-testid="bulk-organic-section">
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#fafafa", display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <TrendingUp size={16} color="#34d399" /> Bulk Organic Traffic
        </h3>
        <p style={{ fontSize: 12, color: "#71717a", marginBottom: 16, lineHeight: 1.5 }}>
          Fetch organic search traffic (via DataForSEO) for all live entries. Comments are resolved to their parent post URL.
          Results are cached — same post URL won't be re-fetched. Post entries also get an organic-traffic trend snapshot so you can see if traffic is rising or dropping over time.
          Empty results (0 traffic, 0 keywords) are NOT cached — they'll be retried next run.
        </p>
        <button
          className="btn btn-primary"
          onClick={handleBulkOrganic}
          disabled={organicRunning}
          data-testid="bulk-organic-trigger-btn"
          style={{ background: "#059669" }}
        >
          {organicRunning ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {organicRunning ? " Running..." : " Fetch Organic Traffic (All Live Entries)"}
        </button>
        {organicProgress && (
          <div style={{
            marginTop: 12, padding: 12, borderRadius: 6, fontSize: 12, lineHeight: 1.6,
            background: organicProgress.status === "failed" ? "rgba(239,68,68,0.1)" : "rgba(16,185,129,0.08)",
            border: `1px solid ${organicProgress.status === "failed" ? "rgba(239,68,68,0.2)" : "rgba(16,185,129,0.15)"}`,
            color: organicProgress.status === "failed" ? "#f87171" : "#a1a1aa",
          }} data-testid="organic-progress">
            {organicProgress.status === "failed" ? (
              <span style={{ color: "#f87171" }}>Failed: {organicProgress.error || "Unknown error"}</span>
            ) : organicProgress.status === "error" ? (
              <span style={{ color: "#f87171" }}>{organicProgress.message}</span>
            ) : organicProgress.status === "completed" ? (
              <span style={{ color: "#34d399" }}>
                Done! Updated <strong>{organicProgress.updated_entries}</strong> entries
                from <strong>{organicProgress.completed_urls}</strong> unique URLs
                {organicProgress.errors > 0 && <>, {organicProgress.errors} errors</>}
              </span>
            ) : (
              <>
                <div style={{ marginBottom: 6 }}>
                  <strong style={{ color: "#fafafa" }}>Processing...</strong> {organicProgress.total_entries || 0} entries queued
                </div>
                <div>Unique URLs: <strong>{organicProgress.unique_urls || "resolving..."}</strong> | Completed: <strong>{organicProgress.completed_urls || 0}</strong>{organicProgress.unique_urls > 0 && <> / {organicProgress.unique_urls}</>} | Updated: <strong>{organicProgress.updated_entries || 0}</strong></div>
                {organicProgress.unique_urls > 0 && (
                  <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: "#27272a", overflow: "hidden" }}>
                    <div style={{ height: "100%", background: "#10b981", borderRadius: 2, width: `${Math.round((organicProgress.completed_urls / organicProgress.unique_urls) * 100)}%`, transition: "width 0.5s" }} />
                  </div>
                )}
                {organicProgress.log && (
                  <pre style={{ marginTop: 8, fontSize: 10, color: "#71717a", fontFamily: "JetBrains Mono", whiteSpace: "pre-wrap", maxHeight: 120, overflow: "auto", background: "#09090b", padding: 8, borderRadius: 4 }}>
                    {organicProgress.log}
                  </pre>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Revalidate Deleted Entries */}
      <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 24, marginBottom: 20 }} data-testid="revalidate-deleted-section">
        <h3 style={{ fontSize: 15, fontWeight: 600, color: "#fafafa", display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <RotateCcw size={16} color="#f59e0b" /> Audit Deleted Entries
        </h3>
        <p style={{ fontSize: 12, color: "#71717a", marginBottom: 16, lineHeight: 1.5 }}>
          Re-check every entry currently marked as <code style={{ color: "#f87171" }}>deleted</code> against Reddit.
          Only flips back to <code style={{ color: "#34d399" }}>live</code> if Reddit explicitly confirms the entry exists — never reverse-flags on API errors or rate-limits.
          Rate-limited to ~100 req/min (safely under Reddit OAuth cap).
        </p>
        <button
          className="btn btn-primary"
          onClick={handleRevalidateDeleted}
          disabled={revalRunning}
          data-testid="revalidate-deleted-btn"
          style={{ background: "#b45309" }}
        >
          {revalRunning ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
          {revalRunning ? " Re-checking..." : " Re-check All Deleted Entries"}
        </button>
        {revalProgress && (
          <div style={{
            marginTop: 12, padding: 12, borderRadius: 6, fontSize: 12, lineHeight: 1.6,
            background: revalProgress.status === "failed" || revalProgress.status === "error" ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.08)",
            border: `1px solid ${revalProgress.status === "failed" || revalProgress.status === "error" ? "rgba(239,68,68,0.2)" : "rgba(245,158,11,0.2)"}`,
            color: "#a1a1aa",
          }} data-testid="revalidate-progress">
            {revalProgress.status === "failed" || revalProgress.status === "error" ? (
              <span style={{ color: "#f87171" }}>Failed: {revalProgress.error || revalProgress.message || "Unknown error"}</span>
            ) : revalProgress.status === "done" ? (
              <span style={{ color: "#a1a1aa" }}>{revalProgress.message}</span>
            ) : revalProgress.status === "completed" ? (
              <div>
                <div style={{ color: "#34d399", marginBottom: 6 }}>
                  Done! Checked <strong>{revalProgress.checked}</strong> of <strong>{revalProgress.total}</strong> entries.
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <span><strong style={{ color: "#34d399" }}>{revalProgress.flipped_to_live}</strong> flipped back to live</span>
                  <span><strong style={{ color: "#f87171" }}>{revalProgress.still_deleted}</strong> confirmed deleted</span>
                  <span><strong style={{ color: "#71717a" }}>{revalProgress.errors}</strong> errors (left as-is)</span>
                </div>
                {revalProgress.flipped_entries && revalProgress.flipped_entries.length > 0 && (
                  <details style={{ marginTop: 10 }}>
                    <summary style={{ cursor: "pointer", color: "#60a5fa", fontSize: 11 }}>
                      Show {revalProgress.flipped_entries.length} flipped entries
                    </summary>
                    <ul style={{ maxHeight: 160, overflow: "auto", marginTop: 6, paddingLeft: 16, fontSize: 11 }}>
                      {revalProgress.flipped_entries.map(e => (
                        <li key={e.id} style={{ color: "#71717a", marginBottom: 2 }}>
                          <a href={e.url} target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>{e.entry_type}</a>: {e.url}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 6 }}>
                  <strong style={{ color: "#fafafa" }}>Re-checking...</strong> {revalProgress.checked || 0} / {revalProgress.total || 0}
                </div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <span><strong style={{ color: "#34d399" }}>{revalProgress.flipped_to_live || 0}</strong> flipped</span>
                  <span><strong style={{ color: "#f87171" }}>{revalProgress.still_deleted || 0}</strong> still deleted</span>
                  <span><strong style={{ color: "#71717a" }}>{revalProgress.errors || 0}</strong> errors</span>
                </div>
                {revalProgress.total > 0 && (
                  <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: "#27272a", overflow: "hidden" }}>
                    <div style={{ height: "100%", background: "#f59e0b", borderRadius: 2, width: `${Math.round((revalProgress.checked / revalProgress.total) * 100)}%`, transition: "width 0.5s" }} />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* AdsPower Section */}
      <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 10, padding: 24, marginBottom: 20 }} data-testid="adspower-settings">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: "#fafafa", display: "flex", alignItems: "center", gap: 8 }}>
            <Shield size={16} color="#60a5fa" /> AdsPower Integration
          </h3>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <span style={{ fontSize: 12, color: form.enabled ? "#10b981" : "#71717a" }}>
              {form.enabled ? "Enabled" : "Disabled"}
            </span>
            <div
              style={{
                width: 40, height: 22, borderRadius: 11, padding: 2,
                background: form.enabled ? "#10b981" : "#374151", cursor: "pointer",
                transition: "background 0.2s",
              }}
              onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
              data-testid="adspower-toggle"
            >
              <div style={{
                width: 18, height: 18, borderRadius: "50%", background: "#fff",
                transform: form.enabled ? "translateX(18px)" : "translateX(0)",
                transition: "transform 0.2s",
              }} />
            </div>
          </label>
        </div>

        <p style={{ fontSize: 12, color: "#71717a", marginBottom: 20, lineHeight: 1.5 }}>
          Connect to AdsPower to manage browser profiles and fetch Reddit post view counts.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: "#a1a1aa", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Key size={12} /> API Key
            </label>
            <div style={{ position: "relative" }}>
              <input className="filter-input" type={showKey ? "text" : "password"} placeholder="Enter your AdsPower API key"
                value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))}
                style={{ width: "100%", paddingRight: 40 }} data-testid="adspower-api-key" />
              <button style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#71717a", cursor: "pointer" }}
                onClick={() => setShowKey(!showKey)} data-testid="toggle-key-visibility">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            {settings?.api_key_masked && (
              <span style={{ fontSize: 11, color: "#52525b", marginTop: 4, display: "block" }}>Current: {settings.api_key_masked}</span>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, color: "#a1a1aa", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Globe size={12} /> API Base URL
            </label>
            <input className="filter-input" type="text" placeholder="http://local.adspower.net:50325"
              value={form.base_url} onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))}
              style={{ width: "100%" }} data-testid="adspower-base-url" />
          </div>

          <div>
            <label style={{ fontSize: 12, color: "#a1a1aa", display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Link2 size={12} /> Default Profile ID (optional)
            </label>
            <input className="filter-input" type="text" placeholder="e.g. jk8h2e1"
              value={form.default_profile_id} onChange={e => setForm(f => ({ ...f, default_profile_id: e.target.value }))}
              style={{ width: "100%" }} data-testid="adspower-profile-id" />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 20, alignItems: "center" }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? " Saving..." : " Save Settings"}
          </button>
          {saved && (
            <span style={{ display: "flex", alignItems: "center", gap: 4, color: "#10b981", fontSize: 13 }}>
              <CheckCircle size={14} /> Saved!
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
